'use client';

import { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/components/i18n-provider';

interface FeaturedSectionProps {
  id: string;
  title: string;
  viewMode?: 'grid' | 'list';
  onSeeAll: () => void;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  children: React.ReactNode;
  className?: string;
}

export function FeaturedSection({
  id,
  title,
  viewMode = 'grid',
  onSeeAll,
  onLoadMore,
  loadingMore,
  children,
  className,
}: FeaturedSectionProps) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Mouse drag state
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const hasDragged = useRef(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || viewMode === 'list') return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState, viewMode, children]);

  // Infinite horizontal scroll: observe sentinel at end of scroll container
  useEffect(() => {
    if (!onLoadMore || viewMode === 'list') return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !loadingMore) {
          onLoadMore();
        }
      },
      { root: scrollRef.current, rootMargin: '0px 200px 0px 0px', threshold: 0 },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [onLoadMore, loadingMore, viewMode]);

  const scroll = useCallback((dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); scroll('left'); }
    if (e.key === 'ArrowRight') { e.preventDefault(); scroll('right'); }
  }, [scroll]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    isDragging.current = true;
    hasDragged.current = false;
    dragStartX.current = e.clientX;
    dragScrollLeft.current = el.scrollLeft;
    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current) return;
    const el = scrollRef.current;
    if (!el) return;
    e.preventDefault();
    const walk = e.clientX - dragStartX.current;
    if (Math.abs(walk) > 3) hasDragged.current = true;
    el.scrollLeft = dragScrollLeft.current - walk;
  }, []);

  const handleMouseUp = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    isDragging.current = false;
    el.style.cursor = '';
    el.style.userSelect = '';
  }, []);

  const handleDragStart = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleClickCapture = useCallback((e: React.MouseEvent) => {
    if (hasDragged.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  return (
    <section id={id} className={cn('space-y-3', className)}>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{title}</h2>
        <button
          onClick={onSeeAll}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-0.5"
        >
          {t('discover.seeAll')}
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>
      {viewMode === 'list' ? (
        <div className="space-y-0.5">
          {children}
        </div>
      ) : (
        <div
          className="relative group/carousel"
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="region"
          aria-label={title}
        >
          {/* Left arrow */}
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-1 top-1/2 -translate-y-1/2 z-20 h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm border border-border shadow-sm flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-accent"
              aria-label={t('discoverCard.scrollLeft')}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}

          {/* Scroll container */}
          <div
            ref={scrollRef}
            className="overflow-x-auto no-scrollbar -mx-3 px-3 py-2 cursor-grab select-none"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onDragStart={handleDragStart}
            onClickCapture={handleClickCapture}
          >
            <div className="flex gap-3 min-w-0 items-stretch">
              {children}
              {/* Sentinel for infinite scroll */}
              <div ref={sentinelRef} className="shrink-0 w-1" aria-hidden="true" />
              {loadingMore && (
                <div className="flex items-center justify-center shrink-0 w-12">
                  <Spinner size="lg" tone="muted" />
                </div>
              )}
            </div>
          </div>

          {/* Right arrow */}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-1 top-1/2 -translate-y-1/2 z-20 h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm border border-border shadow-sm flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-accent"
              aria-label={t('discoverCard.scrollRight')}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </section>
  );
}
